import { logReportDownload } from '../../../util/auditLogger';
import React, { useState, useMemo } from "react";
import * as xlsx from "xlsx";
import { FiSearch, FiDownload } from "react-icons/fi";
import { RiFireLine, RiStackLine } from "react-icons/ri";
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from "../../../components";

export const LastTwoDigitsStockPanel = ({
  currentLottoLastTwo = {},
  currentBlocks = {},
  roundDate = "",
  limitLow = 5,
  limitHigh = 5,
  compact = false,
  className = "mt-3",
}) => {
  const [numberSearchQuery, setNumberSearchQuery] = useState("");
  const [onlyAvailableInLowStock, setOnlyAvailableInLowStock] = useState(false);

  const calculatePercent = (val, total) => {
    if (!total || total === 0) return "0.0%";
    return `${((val / total) * 100).toFixed(1)}%`;
  };

  const lottoLastTwoStats = useMemo(() => {
    const raw = currentLottoLastTwo || {};
    const fullList = [];
    let totalRemainingTickets = 0;
    let soldOutCount = 0;

    for (let i = 0; i <= 99; i++) {
      const num = i < 10 ? `0${i}` : `${i}`;
      const count = Number(raw[num] || 0);
      fullList.push({ number: num, count });
      totalRemainingTickets += count;
      if (count === 0) soldOutCount++;
    }

    const lowStockBase = onlyAvailableInLowStock
      ? fullList.filter((item) => item.count > 0)
      : fullList;

    const topLow = [...lowStockBase]
      .sort((a, b) => {
        if (a.count !== b.count) return a.count - b.count;
        return a.number.localeCompare(b.number);
      })
      .slice(0, limitLow)
      .map((item, idx) => ({
        ...item,
        rank: idx + 1,
      }));

    const topHigh = [...fullList]
      .sort((a, b) => {
        if (b.count !== a.count) return b.count - a.count;
        return a.number.localeCompare(b.number);
      })
      .slice(0, limitHigh)
      .map((item, idx) => ({
        ...item,
        rank: idx + 1,
      }));

    return {
      fullList,
      topLow,
      topHigh,
      totalRemainingTickets,
      soldOutCount,
      activeNumbersCount: 100 - soldOutCount,
    };
  }, [currentLottoLastTwo, onlyAvailableInLowStock, limitLow, limitHigh]);

  const blockStats = useMemo(() => {
    const raw = currentBlocks || {};
    const entries = Object.entries(raw)
      .map(([sizeStr, setCount]) => {
        const size = Number(sizeStr);
        const sets = Number(setCount) || 0;
        const tickets = sets * size;
        return {
          size,
          sets,
          tickets,
        };
      })
      .filter((item) => !isNaN(item.size) && item.sets > 0);

    entries.sort((a, b) => a.size - b.size);

    const totalSets = entries.reduce((acc, curr) => acc + curr.sets, 0);
    const totalTickets = entries.reduce((acc, curr) => acc + curr.tickets, 0);

    const items = entries.map((item) => ({
      ...item,
      label: item.size === 1 ? "ใบเดี่ยว (1 ใบ)" : `ชุด ${item.size} ใบ`,
      percent: totalTickets > 0 ? `${((item.tickets / totalTickets) * 100).toFixed(1)}%` : "0.0%",
    }));

    return {
      items,
      totalSets,
      totalTickets,
    };
  }, [currentBlocks]);

  const handleExportLastTwoExcel = () => {
    if (!lottoLastTwoStats?.fullList || lottoLastTwoStats.fullList.length === 0) return;
    const wb = xlsx.utils.book_new();

    if (blockStats.items.length > 0) {
      const wsSetsData = blockStats.items.map((item) => ({
        "รูปแบบเลขชุด": item.label,
        "จำนวนชุด": item.sets,
        "จำนวนใบรวม": item.tickets,
        "สัดส่วนในสต็อก": item.percent,
      }));
      wsSetsData.push({
        "รูปแบบเลขชุด": "รวมทั้งหมด",
        "จำนวนชุด": blockStats.totalSets,
        "จำนวนใบรวม": blockStats.totalTickets,
        "สัดส่วนในสต็อก": "100.0%",
      });
      const wsSets = xlsx.utils.json_to_sheet(wsSetsData);
      xlsx.utils.book_append_sheet(wb, wsSets, "สรุปเลขชุด");
    }

    const ws1Data = lottoLastTwoStats.topLow.map((item) => ({
      "อันดับ": item.rank,
      "เลขท้าย 2 ตัว": item.number,
      "จำนวนคงเหลือ (ใบ)": item.count,
      "สถานะ": item.count === 0 ? "หมดแล้ว" : item.count <= 5 ? "ใกล้หมด" : "เหลือน้อย",
    }));
    const ws1 = xlsx.utils.json_to_sheet(ws1Data);
    xlsx.utils.book_append_sheet(wb, ws1, `Top ${limitLow} ใกล้หมด`);

    const ws2Data = lottoLastTwoStats.topHigh.map((item) => ({
      "อันดับ": item.rank,
      "เลขท้าย 2 ตัว": item.number,
      "จำนวนคงเหลือ (ใบ)": item.count,
      "สัดส่วนในสต็อก": calculatePercent(item.count, lottoLastTwoStats.totalRemainingTickets),
    }));
    const ws2 = xlsx.utils.json_to_sheet(ws2Data);
    xlsx.utils.book_append_sheet(wb, ws2, `Top ${limitHigh} คงเหลือสูงสุด`);

    const ws3Data = lottoLastTwoStats.fullList.map((item) => ({
      "เลขท้าย 2 ตัว": item.number,
      "จำนวนคงเหลือ (ใบ)": item.count,
    }));
    const ws3 = xlsx.utils.json_to_sheet(ws3Data);
    xlsx.utils.book_append_sheet(wb, ws3, "เลขท้าย 00-99 ทั้งหมด");

    const roundClean = roundDate ? String(roundDate).replace(/-/g, "") : "unknown";
    logReportDownload();
    xlsx.writeFile(wb, `รายงานสต็อกเลขท้าย2ตัว_งวด_${roundClean}.xlsx`);
  };

  // ──────────────────────────────────────────────
  // COMPACT VIEW (เมื่อต้องการลดขนาดลง ไม่ให้เด่นเกินไป เช่น ในหน้า Admin Center)
  // ──────────────────────────────────────────────
  if (compact) {
    return (
      <div className={`bg-white rounded-md p-2.5 shadow-2xs border border-gray-200 ${className}`}>
        {/* Compact Header */}
        <div className="flex flex-wrap items-center gap-2 border-b border-gray-100 pb-2 mb-2.5">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded bg-slate-100 border border-slate-200 text-slate-600 flex items-center justify-center text-xs shrink-0">
              <RiFireLine />
            </div>
            <div className="flex items-center gap-1.5 flex-wrap">
              <h4 className="font-bold text-xs text-gray-800">
                สต็อกเลขท้าย 2 ตัว (Top {limitHigh})
              </h4>
              <span className="text-[9px] font-semibold px-1.5 py-0.2 rounded bg-slate-100 text-slate-500 border border-slate-200">
                อัปเดต 5 นาที
              </span>
            </div>
          </div>

          {/* Controls: Quick Stats, Search, Download */}

          <div className="flex items-center gap-1 text-[11px] bg-slate-50 border border-slate-200 px-2 py-0.5 rounded">
            <span className="text-slate-400">หมด:</span>
            <span className="font-bold text-rose-600">{lottoLastTwoStats.soldOutCount}</span>
            <span className="text-slate-300">|</span>
            <span className="text-slate-400">มีสลาก:</span>
            <span className="font-bold text-emerald-600">{lottoLastTwoStats.activeNumbersCount}</span>
          </div>

          {/* Compact Search Bar */}
          {/* <div className="relative">
              <FiSearch className="absolute left-2 top-1.5 text-[10px] text-slate-400" />
              <input
                type="text"
                maxLength={2}
                placeholder="ค้นหาเลข..."
                value={numberSearchQuery}
                onChange={(e) => setNumberSearchQuery(e.target.value.replace(/\D/g, ""))}
                className="w-24 h-6 pl-5 pr-4 text-[11px] font-mono font-bold bg-slate-50 border border-slate-200 rounded focus:outline-none focus:border-blue-500 focus:bg-white transition"
              />
              {numberSearchQuery && (
                <button
                  type="button"
                  onClick={() => setNumberSearchQuery("")}
                  className="absolute right-1.5 top-1 text-[9px] text-slate-400 hover:text-slate-600 cursor-pointer"
                >
                  ✕
                </button>
              )}
            </div> */}

          {/* Compact Download Button */}
          {/* <button
              type="button"
              onClick={handleExportLastTwoExcel}
              className="flex items-center gap-1 bg-white hover:bg-slate-50 text-slate-700 border border-slate-200 px-2 py-0.5 rounded text-[11px] font-semibold transition shadow-2xs cursor-pointer"
              title="ดาวน์โหลดรายงานสรุปเลขท้าย 2 ตัวเป็น Excel"
            >
              <FiDownload className="text-[10px]" />
              <span className="hidden sm:inline">ดาวน์โหลด Excel</span>
            </button> */}

        </div>

        {/* Compact Search Result Bar */}
        {/* {numberSearchQuery && (
          <div className=" p-2 bg-blue-50/60 border border-blue-200 rounded flex items-center gap-2 text-xs">
            <div className="flex items-center gap-2">
              <span className="px-2 py-0.2 bg-blue-600 text-white font-mono font-bold text-xs rounded">
                {numberSearchQuery.padStart(2, "0")}
              </span>
              <span className="text-[11px] text-blue-900 font-medium">
                คงเหลือในระบบ:
              </span>
            </div>
            <div className="flex items-center gap-1">
              <span className="text-sm font-bold text-blue-800 font-mono">
                {(currentLottoLastTwo?.[numberSearchQuery.padStart(2, "0")] || 0).toLocaleString()}
              </span>
              <span className="text-[10px] text-gray-500">ใบ</span>
            </div>
          </div>
        )} */}

        {/* Top High Stock Only */}
        <div className="border border-slate-200 rounded bg-white overflow-hidden flex flex-col flex-1">
          <div className="p-1.5 px-2.5 bg-slate-50 border-b border-slate-200 flex items-center justify-between gap-2">
            <div className="flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
              <span className="text-[11px] font-bold text-slate-700">
                Top {limitHigh} คงเหลือสูงสุด
              </span>
            </div>
            <span className="text-[9px] font-semibold px-1.5 py-0.2 rounded bg-emerald-50 text-emerald-700 border border-emerald-200">
              พร้อมขาย
            </span>
          </div>

          <div className="overflow-x-auto">
            <Table>
              <TableHeader className="bg-slate-50/50">
                <TableRow className="border-b border-slate-100">
                  <TableHead className="w-8 text-center text-[10px] font-semibold text-slate-500 py-1">#</TableHead>
                  <TableHead className="text-[10px] font-semibold text-slate-500 py-1">เลขท้าย</TableHead>
                  <TableHead className="text-right text-[10px] font-semibold text-slate-500 py-1">คงเหลือ</TableHead>
                  <TableHead className="w-20 text-right text-[10px] font-semibold text-slate-500 py-1">สัดส่วน</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {lottoLastTwoStats.topHigh.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center py-3 text-gray-400 text-xs">
                      ไม่พบข้อมูล
                    </TableCell>
                  </TableRow>
                ) : (
                  lottoLastTwoStats.topHigh.map((item) => {
                    const pct = calculatePercent(item.count, lottoLastTwoStats.totalRemainingTickets);
                    return (
                      <TableRow
                        key={item.number}
                        className="border-b border-slate-100/80 hover:bg-slate-50/60 transition text-xs"
                      >
                        <TableCell className="py-1 text-center font-mono text-[10px] text-slate-400">
                          {item.rank}
                        </TableCell>
                        <TableCell className="py-1">
                          <span className="font-mono font-bold text-xs px-1.5 py-0.2 rounded bg-white border border-slate-200 text-slate-800">
                            {item.number}
                          </span>
                        </TableCell>
                        <TableCell className="py-1 text-right font-mono text-xs font-semibold text-slate-700">
                          {item.count.toLocaleString()}
                          <span className="text-[9px] text-slate-400 ml-1 font-sans">ใบ</span>
                        </TableCell>
                        <TableCell className="py-1 text-right font-mono text-[10px] text-slate-500">
                          <div className="flex items-center justify-end gap-1">
                            <div className="w-8 bg-slate-200 h-1 rounded-full overflow-hidden hidden sm:block">
                              <div
                                className="bg-emerald-500 h-full rounded-full"
                                style={{ width: pct }}
                              />
                            </div>
                            <span>{pct}</span>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      </div>
    );
  }

  // ──────────────────────────────────────────────
  // STANDARD VIEW (สำหรับหน้า Dashboard หลัก)
  // ──────────────────────────────────────────────
  return (
    <div className={`bg-white rounded-md p-3.5 shadow-sm border border-gray-200 ${className}`}>
      {/* Section Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 pb-3 mb-3">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-md bg-rose-50 border border-rose-200 flex items-center justify-center text-rose-600 shadow-sm">
            <RiFireLine className="text-lg" />
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-bold text-sm text-gray-900">
                วิเคราะห์สต็อกสลากเลขท้าย 2 ตัว (00 - 99)
              </h3>
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-md bg-rose-50 text-rose-700 border border-rose-200">
                อัปเดตทุก 5 นาที
              </span>
            </div>
            <p className="text-[11px] text-gray-500 font-medium mt-0.5">
              อันดับเลขท้าย 2 ตัวที่ใกล้จะหมด และเลขท้ายที่มีสต็อกคงเหลือมากที่สุดประจำงวด
            </p>
          </div>
        </div>

        {/* Quick Stats, Search Bar & Excel Download */}
        <div className="flex items-center gap-2.5 flex-wrap">
          <div className="flex items-center gap-1.5 text-xs bg-slate-50 border border-slate-200 px-2.5 py-1 rounded-md">
            <span className="text-slate-500 font-medium">หมดแล้ว:</span>
            <span className="font-bold text-rose-600">{lottoLastTwoStats.soldOutCount} เลข</span>
            <span className="text-slate-300">|</span>
            <span className="text-slate-500 font-medium">ยังมีขาย:</span>
            <span className="font-bold text-emerald-600">{lottoLastTwoStats.activeNumbersCount} เลข</span>
          </div>

          {/* Quick Number Lookup Input */}
          {/* <div className="relative">
            <FiSearch className="absolute left-2.5 top-2 text-xs text-slate-400" />
            <input
              type="text"
              maxLength={2}
              placeholder="ค้นหาเลข (เช่น 79)..."
              value={numberSearchQuery}
              onChange={(e) => setNumberSearchQuery(e.target.value.replace(/\D/g, ""))}
              className="w-36 h-8 pl-7 pr-6 text-xs font-mono font-bold bg-slate-50 border border-slate-300 rounded-md focus:outline-none focus:border-blue-500 focus:bg-white transition"
            />
            {numberSearchQuery && (
              <button
                type="button"
                onClick={() => setNumberSearchQuery("")}
                className="absolute right-2 top-2 text-[10px] text-slate-400 hover:text-slate-600 cursor-pointer"
              >
                ✕
              </button>
            )}
          </div> */}

          {/* Download Excel Button */}
          {/* <button
            type="button"
            onClick={handleExportLastTwoExcel}
            className="flex items-center gap-1.5 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border border-emerald-300 px-2.5 py-1.5 rounded-md text-xs font-bold transition shadow-2xs cursor-pointer"
            title="ดาวน์โหลดรายงานสรุปเลขท้าย 2 ตัวเป็น Excel"
          >
            <FiDownload className="text-xs" />
            <span className="hidden sm:inline">ดาวน์โหลด Excel</span>
          </button> */}
        </div>
      </div>

      {/* If Searching, show search result card */}
      {numberSearchQuery && (
        <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-md flex items-center justify-between gap-3 text-xs">
          <div className="flex items-center gap-3">
            <span className="px-3 py-1 bg-blue-600 text-white font-mono font-black text-base rounded-md shadow-xs">
              {numberSearchQuery.padStart(2, "0")}
            </span>
            <div>
              <p className="font-bold text-blue-950">
                ผลการค้นหาเลขท้าย 2 ตัว:{" "}
                <span className="font-mono text-blue-700">
                  {numberSearchQuery.padStart(2, "0")}
                </span>
              </p>
              <p className="text-[11px] text-blue-700">
                สถานะสต็อกปัจจุบันในระบบ
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500">คงเหลือ:</span>
            <span className="text-base font-black text-blue-800">
              {(currentLottoLastTwo?.[numberSearchQuery.padStart(2, "0")] || 0).toLocaleString()}
            </span>
            <span className="text-xs font-semibold text-gray-600">ใบ</span>
          </div>
        </div>
      )}

      {/* Grid 2 Columns: Top Low Stock (Left) & Top High Stock (Right) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3.5">
        {/* Column 1: Top Low Stock */}
        <div className="border border-rose-200 rounded-md bg-rose-50/20 overflow-hidden flex flex-col">
          <div className="p-2.5 bg-rose-50/80 border-b border-rose-200 flex items-center justify-between gap-2 flex-wrap">
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-rose-500"></span>
              <h4 className="text-xs font-bold text-rose-950">
                Top {limitLow} เลขท้าย 2 ตัวใกล้จะหมด
              </h4>
            </div>
            <div className="flex items-center gap-1.5 text-[11px]">
              <label className="flex items-center gap-1.5 text-rose-800 cursor-pointer select-none font-medium">
                <input
                  type="checkbox"
                  checked={onlyAvailableInLowStock}
                  onChange={(e) => setOnlyAvailableInLowStock(e.target.checked)}
                  className="rounded text-rose-600 focus:ring-rose-500 w-3.5 h-3.5 cursor-pointer"
                />
                <span>เฉพาะที่ยังมีสลาก (&gt; 0 ใบ)</span>
              </label>
            </div>
          </div>

          <div className="max-h-[460px] overflow-y-auto">
            <Table>
              <TableHeader className="sticky top-0 bg-rose-100/95 backdrop-blur-xs z-10 shadow-2xs">
                <TableRow className="border-b border-rose-200">
                  <TableHead className="w-12 text-center text-[10px] font-bold text-rose-900 py-1.5">#</TableHead>
                  <TableHead className="text-[10px] font-bold text-rose-900 py-1.5">เลขท้าย 2 ตัว</TableHead>
                  <TableHead className="text-right text-[10px] font-bold text-rose-900 py-1.5">คงเหลือ</TableHead>
                  <TableHead className="w-24 text-center text-[10px] font-bold text-rose-900 py-1.5">สถานะ</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {lottoLastTwoStats.topLow.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center py-6 text-gray-400 text-xs">
                      ไม่พบข้อมูล
                    </TableCell>
                  </TableRow>
                ) : (
                  lottoLastTwoStats.topLow.map((item) => {
                    const isZero = item.count === 0;
                    const isVeryLow = item.count > 0 && item.count <= 5;
                    return (
                      <TableRow
                        key={item.number}
                        className="border-b border-rose-100/60 hover:bg-rose-100/40 transition text-xs"
                      >
                        <TableCell className="py-1.5 text-center font-mono text-[11px] font-bold text-slate-400">
                          {item.rank}
                        </TableCell>
                        <TableCell className="py-1.5">
                          <span className="font-mono font-black text-sm px-2 py-0.5 rounded bg-white border border-slate-200 text-slate-800 shadow-2xs">
                            {item.number}
                          </span>
                        </TableCell>
                        <TableCell className="py-1.5 text-right font-mono font-bold">
                          <span className={isZero ? "text-slate-400" : isVeryLow ? "text-rose-600 font-black" : "text-amber-700"}>
                            {item.count.toLocaleString()}
                          </span>
                          <span className="text-[10px] text-slate-400 ml-1 font-sans">ใบ</span>
                        </TableCell>
                        <TableCell className="py-1.5 text-center">
                          {isZero ? (
                            <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-slate-100 text-slate-500 border border-slate-200">
                              หมดแล้ว
                            </span>
                          ) : isVeryLow ? (
                            <span className="px-1.5 py-0.5 rounded text-[10px] font-extrabold bg-rose-100 text-rose-700 border border-rose-300 animate-pulse">
                              ใกล้หมด
                            </span>
                          ) : (
                            <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-amber-100 text-amber-800 border border-amber-300">
                              เหลือน้อย
                            </span>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </div>

        {/* Column 2: Top High Stock */}
        <div className="border border-emerald-200 rounded-md bg-emerald-50/20 overflow-hidden flex flex-col">
          <div className="p-2.5 bg-emerald-50/80 border-b border-emerald-200 flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
              <h4 className="text-xs font-bold text-emerald-950">
                Top {limitHigh} เลขท้าย 2 ตัวคงเหลือมากที่สุด
              </h4>
            </div>
            <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-emerald-100 text-emerald-800 border border-emerald-300">
              สต็อกพร้อมขาย
            </span>
          </div>

          <div className="max-h-[460px] overflow-y-auto">
            <Table>
              <TableHeader className="sticky top-0 bg-emerald-100/95 backdrop-blur-xs z-10 shadow-2xs">
                <TableRow className="border-b border-emerald-200">
                  <TableHead className="w-12 text-center text-[10px] font-bold text-emerald-900 py-1.5">#</TableHead>
                  <TableHead className="text-[10px] font-bold text-emerald-900 py-1.5">เลขท้าย 2 ตัว</TableHead>
                  <TableHead className="text-right text-[10px] font-bold text-emerald-900 py-1.5">คงเหลือ</TableHead>
                  <TableHead className="w-28 text-right text-[10px] font-bold text-emerald-900 py-1.5">สัดส่วนในสต็อก</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {lottoLastTwoStats.topHigh.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center py-6 text-gray-400 text-xs">
                      ไม่พบข้อมูล
                    </TableCell>
                  </TableRow>
                ) : (
                  lottoLastTwoStats.topHigh.map((item) => {
                    const pct = calculatePercent(item.count, lottoLastTwoStats.totalRemainingTickets);
                    const isTop3 = item.rank <= 3;
                    return (
                      <TableRow
                        key={item.number}
                        className="border-b border-emerald-100/60 hover:bg-emerald-100/40 transition text-xs"
                      >
                        <TableCell className="py-1.5 text-center font-mono text-[11px] font-bold">
                          {isTop3 ? (
                            <span className={`inline-flex items-center justify-center w-5 h-5 rounded-full text-[10px] font-black text-white shadow-2xs ${item.rank === 1 ? "bg-amber-500" : item.rank === 2 ? "bg-slate-400" : "bg-amber-700"
                              }`}>
                              {item.rank}
                            </span>
                          ) : (
                            <span className="text-slate-400">{item.rank}</span>
                          )}
                        </TableCell>
                        <TableCell className="py-1.5">
                          <span className="font-mono font-black text-sm px-2 py-0.5 rounded bg-white border border-emerald-200 text-emerald-900 shadow-2xs">
                            {item.number}
                          </span>
                        </TableCell>
                        <TableCell className="py-1.5 text-right font-mono font-bold text-emerald-700">
                          {item.count.toLocaleString()}
                          <span className="text-[10px] text-slate-400 ml-1 font-sans">ใบ</span>
                        </TableCell>
                        <TableCell className="py-1.5 text-right font-mono text-[11px] text-slate-500">
                          <div className="flex items-center justify-end gap-1.5">
                            <div className="w-12 bg-slate-200 h-1.5 rounded-full overflow-hidden hidden sm:block">
                              <div
                                className="bg-emerald-500 h-full rounded-full"
                                style={{ width: pct }}
                              />
                            </div>
                            <span>{pct}</span>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LastTwoDigitsStockPanel;
